import { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const useFrdGenerator = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [generatedData, setGeneratedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Upload FRD document
  const uploadDocument = async () => {
    if (!uploadedFile) return;

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('frdDocument', uploadedFile);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/frd/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      setUploadedDocument(response.data.data);
      return response.data.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload document');
      //console.error('Upload error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Generate documentation
  const generateDocumentation = async (documentId, userPrompt) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/frd/generate/${documentId}`,
        {userPrompt:userPrompt,
        },{
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Transform the backend response to match frontend structure
      const backendData = response.data.data;
      const transformedData = transformBackendData(backendData);
      
      setGeneratedData(transformedData);
      return transformedData;
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.code === 'AI_PLAN_UPGRADE_REQUIRED') {
        const upgradeErr = {
          message: errData.message || 'AI features are not included in your current plan. Please upgrade to unlock AI.',
          severity: 'warning',
          upgrade_required: true
        };
        setError(upgradeErr);
        window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI TRD Generation" } }));
        throw upgradeErr;
      } else if (errData?.code === 'AI_TOKENS_EXHAUSTED') {
        const tokenErr = {
          message: `⚠️ ${errData.message || 'You have used all your AI tokens for this billing cycle.'}`,
          severity: 'warning'
        };
        setError(tokenErr);
        throw tokenErr;
      } else {
        const generalErr = errData?.message || err.message || 'Failed to generate documentation';
        setError(generalErr);
        //console.error('Generation error:', err);
        throw err;
      }
    } finally {
      setLoading(false);
    }
  };

  // Transform backend data to frontend format
  const transformBackendData = (backendData) => {
    const { generatedDocuments } = backendData;
    
    const transformed = {
      trd: null
    };

    generatedDocuments.forEach(doc => {
      if (doc.document_type === 'trd') {
        transformed.trd = doc.content;
      }
    });

    return transformed;
  };

  // Get generated documents
  const getGeneratedDocuments = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/frd/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const transformedData = transformBackendData(response.data.data);
      setGeneratedData(transformedData);
      return transformedData;
    } catch (err) {
      setError('Failed to fetch generated documents');
      //console.error('Fetch error:', err);
      throw err;
    }
  };

// In useFrdGenerator.js - update downloadDocument function
// const downloadDocument = async (documentId) => {
//   try {
//     const token = localStorage.getItem('token');
//     const response = await axios.get(`${API_URL}/api/frd/download/trd/${documentId}`, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//       responseType: 'blob'
//     });

//     // Create proper blob for DOCX
//     const blob = new Blob([response.data], { 
//       type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
//     });
    
//     const url = window.URL.createObjectURL(blob);
//     const link = document.createElement('a');
//     link.href = url;
//     link.setAttribute('download', `Technical_Requirements_Document_${documentId}.txt`);
//     document.body.appendChild(link);
//     link.click();
    
//     // Cleanup
//     document.body.removeChild(link);
//     window.URL.revokeObjectURL(url);
    
//     return true;
//   } catch (err) {
//     setError('Failed to download document');
//     //console.error('Download error:', err);
//     return false;
//   }
// };
// In useFrdGenerator.js - Update downloadDocument function

const downloadDocument = async (documentId) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.get(`${API_URL}/api/frd/download/trd/${documentId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob'
    });

    // Create download link for PDF
    const blob = new Blob([response.data], { 
      type: 'application/pdf' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Technical_Requirements_Document_${documentId}.pdf`);
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (err) {
    //console.error('Download error:', err);
    setError('Failed to download document');
    return false;
  }
};



const generateFRD = async (projectTitle, requirementsPrompt) => {
  setLoading(true);
  setError(null);

  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API_URL}/api/frd/generate-frd`,
      {
        projectTitle,
        requirementsPrompt
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data.data;
  } catch (err) {
    const errData = err.response?.data;
    if (errData?.code === 'AI_PLAN_UPGRADE_REQUIRED') {
      const upgradeErr = {
        message: errData.message || 'AI features are not included in your current plan. Please upgrade to unlock AI.',
        severity: 'warning',
        upgrade_required: true
      };
      setError(upgradeErr);
      window.dispatchEvent(new CustomEvent("openUpgradeModal", { detail: { feature: "AI FRD Generation" } }));
      throw upgradeErr;
    } else if (errData?.code === 'AI_TOKENS_EXHAUSTED') {
      const tokenErr = {
        message: `⚠️ ${errData.message || 'You have used all your AI tokens for this billing cycle.'}`,
        severity: 'warning'
      };
      setError(tokenErr);
      throw tokenErr;
    } else {
      const generalErr = errData?.message || err.message || 'Failed to generate FRD';
      setError(generalErr);
      //console.error('FRD Generation error:', err);
      throw err;
    }
  } finally {
    setLoading(false);
  }
};

// Download FRD as DOCX - Updated with project name in filename
const downloadFRD = async (title, content, projectTitle) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.post(
      `${API_URL}/api/frd/download-frd-docx`,
      {
        title,
        content
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob'
      }
    );

    // Create filename with project name
    const sanitizedProjectName = projectTitle 
      ? projectTitle.replace(/[^a-zA-Z0-9]/g, '_') // Remove special characters
      : 'Functional_Requirements';
    
    const filename = `${sanitizedProjectName}_FRD_${new Date().toISOString().split('T')[0]}.docx`;
    
    // Create download link for DOCX
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (err) {
    //console.error('Download error:', err);
    setError('Failed to download document');
    return false;
  }
};
const downloadTRDDocx = async (documentId) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.get(`${API_URL}/api/frd/download/trd-docx/${documentId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob'
    });

    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Technical_Requirements_Document_${documentId}.docx`);
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (err) {
    //console.error('Download DOCX error:', err);
    setError('Failed to download document');
    return false;
  }
};

  const clearAll = () => {
    setUploadedFile(null);
    setUploadedDocument(null);
    setGeneratedData(null);
    setError(null);
  };

  return {
    uploadedFile,
    setUploadedFile,
    uploadedDocument,
    generatedData,
    loading,
    error,
    setError,
    uploadDocument,
    generateDocumentation,
    getGeneratedDocuments,
    downloadDocument,
    generateFRD,
    downloadFRD,
    downloadTRDDocx,
    clearAll
  };
};